import type { OrganizationCustomProduct } from '@breakoutlearning/firebase-repository/models/OrganizationCustomProduct'
import type { SectionRequest } from '@breakoutlearning/firebase-repository/models/SectionRequest'
import {
  formatInvoiceRequestEstimatedCost,
  isInvoiceRequestProductPriced,
} from '@breakoutlearning/firebase-repository/sources-of-truth/invoiceRequestEstimatedCost'
import { useRepository } from 'hooks/auth'
import { useBreakoutUser } from 'hooks/profile'
import { useMemo } from 'react'

export function useInvoiceRequestEstimatedCost(
  request: SectionRequest,
  customProducts: OrganizationCustomProduct[],
  sectionProductId: string | undefined
) {
  const user = useBreakoutUser()
  const repository = useRepository()
  const defaultProducts = user.defaultSystemProducts.isLoaded
    ? user.defaultSystemProducts.value.models
    : []

  const products = [...defaultProducts, ...customProducts]
  const product = sectionProductId
    ? products.find((product) => sectionProductId === product.id)
    : undefined

  const isProductPriced = isInvoiceRequestProductPriced(
    product?.data.sectionPassPrice
  )

  // only expose the product details when the product actually drives the
  // estimate, so callers cannot render a product row next to a token-priced
  // cost (BL-4756)
  const sectionPassPriceCents = isProductPriced
    ? product?.data.sectionPassPrice
    : undefined

  const tokenPrice = repository.pricing.tokenPriceInt

  // tokenPriceInt falls back to defaultTokenPrice ($10) until pricing streams
  // in, so hold the estimate rather than briefly rendering $10-based numbers
  // on an org configured differently. Product pricing does not read it.
  const isReady = isProductPriced || repository.pricing.isLoaded

  const estimatedCost = useMemo(
    () =>
      isReady
        ? formatInvoiceRequestEstimatedCost({
            studentCount: request.data.sectionRequestStudentCount,
            assignmentCount: request.data.sectionRequestAssignmentCount,
            tokenPrice,
            sectionPassPriceCents,
          })
        : '',
    [
      isReady,
      request.data.sectionRequestStudentCount,
      request.data.sectionRequestAssignmentCount,
      tokenPrice,
      sectionPassPriceCents,
    ]
  )

  return {
    estimatedCost,
    isProductPriced,
    sectionPassPriceCents,
    productName: isProductPriced ? product?.data.productName : undefined,
  }
}
